import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import {
  Container,
  Card,
  Form,
  Button,
  Row,
  Col,
  Modal,
  InputGroup,
  Spinner,
} from 'react-bootstrap';
import DataTable from 'react-data-table-component';
import { FaEdit, FaTrash, FaFileExcel, FaBoxOpen } from 'react-icons/fa';
import axios from 'axios';
import BASE_URL from '../config';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import './AdminLayout.css';

// ********** IMPORT MAP COMPONENT **********
// Assuming Map.js is in src/components/
import MapPopup from '../components/Map';
// ******************************************

// --- Configuration & Utilities ---

const defaultCenter = [24.4667, 54.3667];

// Axios instance (unchanged)
const authAxios = axios.create({ baseURL: BASE_URL });
authAxios.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ================= MAIN COMPONENT =================
const AdminBranches = ({ onClose }) => {
  // Listing / pagination
  const [allBranches, setAllBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(20);
  const [hasMore, setHasMore] = useState(true);
  const [lastPage, setLastPage] = useState(1);
  const isFetchingRef = useRef(false);

  // Managers for dropdowns
  const [managers, setManagers] = useState([]);

  // Create modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({
    name: '',
    code: '',
    address: '',
    latitude: '',
    longitude: '',
    phone: '',
    managerId: '',
    radius: '',
  });
  const [createLoading, setCreateLoading] = useState(false);
  const [createMapCenter, setCreateMapCenter] = useState(defaultCenter);
  const [showCreateMapPopup, setShowCreateMapPopup] = useState(false);

  // Edit modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [currentBranch, setCurrentBranch] = useState(null);
  const [showEditMapPopup, setShowEditMapPopup] = useState(false);
  const [editMapCenter, setEditMapCenter] = useState(defaultCenter);

  // Message Modal
  const [messageModal, setMessageModal] = useState({
    show: false,
    title: '',
    body: '',
  });

  // Map Language State
  const [mapLanguage, setMapLanguage] = useState('en');

  // Helper functions for the message modal
  const showMessageModal = (title, body) => {
    setMessageModal({ show: true, title, body });
  };

  const handleCloseMessageModal = () => {
    setMessageModal({ show: false, title: '', body: '' });
  };

  // MapPopup shared handler
  const handleMapSelectForCreate = (coords, address) => {
    setCreateForm((prev) => ({
      ...prev,
      latitude: coords[0].toString(),
      longitude: coords[1].toString(),
      address,
    }));
    setShowCreateMapPopup(false);
  };

  const handleMapSelectForEdit = (coords, address) => {
    setCurrentBranch((prev) => ({
      ...prev,
      // Ensure lat and lng are updated correctly (names used in state and API payload)
      lat: coords[0].toString(),
      lng: coords[1].toString(),
      address,
    }));
    setShowEditMapPopup(false);
  };

  // Fetch a single page of branches
  const fetchBranchesPage = async (page, rowsPerPage) => {
    const params = {
      per_page: rowsPerPage,
    };

    const res = await authAxios.get(`/showrooms?page=${page}`, { params });
    const payload = res.data || {};
    const responseData = payload?.data || payload;
    const branchesData = Array.isArray(responseData?.data)
      ? responseData.data
      : Array.isArray(responseData)
      ? responseData
      : [];

    // Extract pagination metadata
    const totalCount = responseData?.total;
    const lastPageNum =
      responseData?.last_page ||
      responseData?.meta?.last_page ||
      res.data?.last_page;
    const currentPageNum =
      responseData?.current_page ||
      responseData?.meta?.current_page ||
      res.data?.current_page ||
      page;

    return {
      branchesData,
      totalCount,
      lastPageNum,
      currentPageNum,
      hasMore: lastPageNum
        ? page < lastPageNum
        : branchesData.length === rowsPerPage,
    };
  };

  // Fetch all branches page by page until last page
  const fetchAllBranchesSequentially = async () => {
    // Prevent multiple simultaneous calls
    if (isFetchingRef.current) {
      return;
    }
    isFetchingRef.current = true;
    setLoading(true);
    setAllBranches([]);
    setCurrentPage(1);
    setHasMore(true);

    try {
      let allBranchesList = [];
      let page = 1;
      let lastPageNum = null;
      let totalCount = 0;

      // Fetch first page to get metadata
      const firstPageData = await fetchBranchesPage(page, perPage);
      allBranchesList = [...firstPageData.branchesData];
      lastPageNum = firstPageData.lastPageNum;
      totalCount = firstPageData.totalCount || allBranchesList.length;

      // Update state with first page data
      setAllBranches(allBranchesList);
      setCurrentPage(page);
      setTotalRows(totalCount);
      if (lastPageNum) {
        setLastPage(lastPageNum);
      }

      // If we have last_page info, fetch remaining pages
      if (lastPageNum && lastPageNum > 1) {
        for (page = 2; page <= lastPageNum; page++) {
          try {
            const pageData = await fetchBranchesPage(page, perPage);
            if (pageData.branchesData.length === 0) {
              break;
            }
            allBranchesList = [...allBranchesList, ...pageData.branchesData];
            setAllBranches([...allBranchesList]);
            setCurrentPage(page);
          } catch (err) {
            console.error(`Error fetching branches page ${page}:`, err);
            break;
          }
        }
      } else if (firstPageData.hasMore && !lastPageNum) {
        // If we don't have last_page info, continue fetching until we get an empty page
        page = 2;
        while (true) {
          try {
            const pageData = await fetchBranchesPage(page, perPage);
            if (pageData.branchesData.length === 0) {
              break;
            }
            allBranchesList = [...allBranchesList, ...pageData.branchesData];
            setAllBranches([...allBranchesList]);
            setCurrentPage(page);

            // If we got fewer items than per_page, it's likely the last page
            if (pageData.branchesData.length < perPage) {
              break;
            }
            page++;
          } catch (err) {
            console.error(`Error fetching branches page ${page}:`, err);
            break;
          }
        }
      }

      setHasMore(false);
      setLastPage(page);
      setTotalRows(allBranchesList.length);
      console.log(
        `Finished fetching all branches. Total: ${allBranchesList.length}`
      );
    } catch (err) {
      console.error('Error fetching all branches:', err);
      setAllBranches([]);
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  };

  // Fetch managers (all pages)
  const fetchManagers = async () => {
    try {
      let acc = [];
      let page = 1;
      let keep = true;
      while (keep) {
        const res = await authAxios.get('/users', { params: { page } });
        const payload = res.data || {};
        const items = payload?.data?.data ?? payload?.data ?? [];
        if (!Array.isArray(items) || items.length === 0) {
          keep = false;
        } else {
          acc = acc.concat(items.filter((u) => u.role === 'branch_manager'));
        }
        const current =
          payload?.data?.current_page ?? payload?.meta?.current_page ?? page;
        const last =
          payload?.data?.last_page ?? payload?.meta?.last_page ?? page;
        if (!current || !last || current >= last) keep = false;
        else page++;
      }
      setManagers(acc);
    } catch (err) {
      console.error('Failed to fetch managers:', err);
      setManagers([]);
    }
  };

  useEffect(() => {
    fetchAllBranchesSequentially();
    fetchManagers();
  }, []);

  // Filter client-side (search by name, code, address)
  const filteredBranches = useMemo(
    () =>
      allBranches.filter((b) =>
        [b.name, b.code, b.address]
          .filter(Boolean)
          .some((v) =>
            v.toString().toLowerCase().includes(search.toLowerCase())
          )
      ),
    [allBranches, search]
  );

  // Refresh branches after create/update/delete
  const refreshBranches = () => {
    fetchAllBranchesSequentially();
  };

  // Removed Intersection Observer - now fetching all pages sequentially

  // Export excel - exports filtered data
  const handleExportToExcel = () => {
    if (!filteredBranches || filteredBranches.length === 0) {
      showMessageModal('Warning', 'No branch data to export.');
      return;
    }

    const exportData = filteredBranches.map((b, idx) => ({
      'S.No': idx + 1,
      Name: b.name,
      Code: b.code,
      Address: b.address,
      Phone: b.phone,
      'Coordinates (Lat, Lng)':
        b.lat !== null &&
        b.lat !== undefined &&
        b.lng !== null &&
        b.lng !== undefined
          ? `${(+b.lat).toFixed(4)}, ${(+b.lng).toFixed(4)}`
          : 'N/A',
      'Manager ID': b.manager_id ?? '',
      'Radius (km)': b.radius ?? '',
      Created: b.created_at ?? '',
      Updated: b.updated_at ?? '',
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Branches');
    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([buf], { type: 'application/octet-stream' });
    saveAs(blob, `branches_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  // Create branch
  const handleCreateChange = (e) => {
    const { name, value } = e.target;
    setCreateForm((prev) => ({ ...prev, [name]: value }));
  };

  const openCreateMap = () => {
    // Set map center based on form data or default UAE center
    const lat = parseFloat(createForm.latitude);
    const lng = parseFloat(createForm.longitude);

    if (!isNaN(lat) && !isNaN(lng)) {
      setCreateMapCenter([lat, lng]);
    } else {
      setCreateMapCenter(defaultCenter);
    }
    setShowCreateMapPopup(true);
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    setCreateLoading(true);
    try {
      const payload = {
        name: createForm.name,
        code: createForm.code,
        address: createForm.address || '',
        lat: createForm.latitude ? parseFloat(createForm.latitude) : 0,
        lng: createForm.longitude ? parseFloat(createForm.longitude) : 0,
        phone: createForm.phone || '',
        manager_id: createForm.managerId
          ? parseInt(createForm.managerId)
          : null,
        radius: createForm.radius ? parseFloat(createForm.radius) : 0,
      };
      await authAxios.post('/showrooms', payload);
      showMessageModal('Success', 'Branch created successfully!');
      setShowCreateModal(false);
      setCreateForm({
        name: '',
        code: '',
        address: '',
        latitude: '',
        longitude: '',
        phone: '',
        managerId: '',
        radius: '',
      });
      // Reset and reload branches from the beginning
      setAllBranches([]);
      setCurrentPage(1);
      setHasMore(true);
      refreshBranches();
    } catch (err) {
      console.error('Failed to create branch:', err.response ?? err);
      showMessageModal('Error', 'Failed to create branch. Please try again.');
    } finally {
      setCreateLoading(false);
    }
  };

  // Edit branch
  const handleEditClick = (branch) => {
    // Ensure lat and lng are always strings for the input fields
    const lat = branch.lat?.toString() || branch.latitude?.toString() || '';
    const lng = branch.lng?.toString() || branch.longitude?.toString() || '';

    setCurrentBranch({
      ...branch,
      lat,
      lng,
    });
    setShowEditModal(true);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setCurrentBranch((prev) => ({ ...prev, [name]: value }));
  };

  const openEditMap = () => {
    // Correctly parse latitude and longitude to numbers before setting the map center
    const lat = parseFloat(currentBranch.lat);
    const lng = parseFloat(currentBranch.lng);

    if (!isNaN(lat) && !isNaN(lng)) {
      setEditMapCenter([lat, lng]);
    } else {
      setEditMapCenter(defaultCenter);
    }
    setShowEditMapPopup(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!currentBranch?.id)
      return showMessageModal('Error', 'No branch selected for update.');
    try {
      const payload = {
        name: currentBranch.name,
        code: currentBranch.code,
        address: currentBranch.address || '',
        lat: currentBranch.lat ? parseFloat(currentBranch.lat) : 0,
        lng: currentBranch.lng ? parseFloat(currentBranch.lng) : 0,
        phone: currentBranch.phone || '',
        manager_id: currentBranch.manager_id
          ? parseInt(currentBranch.manager_id)
          : null,
        radius: currentBranch.radius ? parseFloat(currentBranch.radius) : 0,
      };
      await authAxios.put(`/showrooms/${currentBranch.id}`, payload);
      showMessageModal('Success', 'Branch updated successfully!');
      setShowEditModal(false);
      // Reset and reload branches from the beginning
      setAllBranches([]);
      setCurrentPage(1);
      setHasMore(true);
      refreshBranches();
    } catch (err) {
      console.error('Failed to update branch:', err.response ?? err);
      showMessageModal('Error', 'Failed to update branch. Please try again.');
    }
  };

  // Delete branch
  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this branch?')) return;
    try {
      await authAxios.delete(`/showrooms/${id}`);
      showMessageModal('Success', 'Branch deleted successfully!');
      // Reset and reload branches from the beginning
      setAllBranches([]);
      setCurrentPage(1);
      setHasMore(true);
      refreshBranches();
    } catch (err) {
      console.error('Delete failed:', err.response ?? err);
      showMessageModal('Error', 'Failed to delete branch.');
    }
  };

  // Custom Loading Component for DataTable
  const CustomLoadingIndicator = (
    <div className='p-3 text-center'>
      <Spinner
        animation='border'
        role='status'
        className='me-2'
        variant='primary'
      />
      <span className='text-primary'>
        {currentPage && lastPage && lastPage > 1
          ? `Loading branches... (Page ${currentPage} of ${lastPage})`
          : 'Loading branches...'}
      </span>
    </div>
  );

  // Custom No Data Component for DataTable
  const CustomNoDataComponent = (
    <div className='p-4 text-center text-muted'>
      <FaBoxOpen className='mb-2' size={32} />
      <h5 className='mt-2'>No Branches Found</h5>
      <p className='mb-0'>
        There are currently no branches in this list or matching your search
        criteria.
      </p>
    </div>
  );

  const columns = [
    {
      name: '#',
      selector: (row, index) => index + 1,
      sortable: true,
      width: '3vw',
      compact: true,
      grow: 0,
    },
    {
      name: 'Name',
      selector: (row) => row.name,
      sortable: true,
      minWidth: '8vw',
      wrap: true,
      grow: 1,
    },
    {
      name: 'Code',
      selector: (row) => row.code,
      sortable: true,
      minWidth: '5vw',
      wrap: true,
      grow: 1,
    },
    {
      name: 'Address',
      selector: (row) => row.address || 'N/A',
      sortable: true,
      minWidth: '12vw',
      wrap: true,
      grow: 2,
      style: {
        wordBreak: 'break-word',
      },
    },
    {
      name: 'Phone',
      selector: (row) => row.phone || 'N/A',
      sortable: true,
      minWidth: '6vw',
      wrap: true,
      grow: 1,
    },
    {
      name: 'Manager',
      selector: (row) => row.manager?.name ?? 'N/A',
      sortable: true,
      minWidth: '8vw',
      wrap: true,
      grow: 1,
    },
    {
      name: 'Radius (km)',
      selector: (row) => row.radius ?? 'N/A',
      sortable: true,
      minWidth: '5vw',
      wrap: true,
      grow: 1,
    },
    {
      name: 'Actions',
      center: true,
      cell: (row) => {
        const buttons = [
          <Button
            key='edit'
            size='sm'
            variant='warning'
            onClick={() => handleEditClick(row)}
            title='Edit branch'
          >
            <FaEdit />
          </Button>,
          <Button
            key='delete'
            size='sm'
            variant='danger'
            onClick={() => handleDelete(row.id)}
            title='Delete branch'
          >
            <FaTrash />
          </Button>,
        ];

        return (
          <div
            style={{
              display: 'flex',
              gap: '8px',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '8px 0',
            }}
          >
            {buttons.map((button) =>
              React.cloneElement(button, {
                style: {
                  ...button.props.style,
                  marginTop: '4px',
                  marginBottom: '4px',
                },
              })
            )}
          </div>
        );
      },
      minWidth: '6vw',
      grow: 0,
    },
  ];

  return (
    <div className='border p-2 rounded' style={{ background: '#fff' }}>
      <Container fluid className='p-4'>
        <h3 style={{ textAlign: 'center' }}>Branch Management</h3>
        {/* Message Modal */}
        <Modal
          show={messageModal.show}
          onHide={handleCloseMessageModal}
          centered
          size='md'
          className='text-center'
        >
          <Modal.Body
            className='py-4'
            style={{
              backgroundColor: '#ffffff',
              color: '#1f1f1f',
              borderRadius: '15px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
            }}
          >
            <div className='mb-3'>
              {messageModal.title === 'Success' ? (
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='60'
                  height='60'
                  fill='green'
                  className='bi bi-check-circle-fill'
                  viewBox='0 0 16 16'
                >
                  <path d='M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM6.97 11.03a.75.75 0 0 0 1.08 0l3.992-3.992a.75.75 0 0 0-1.08-1.04L7.5 9.439 5.53 7.47a.75.75 0 0 0-1.08 1.04l2.52 2.52z' />
                </svg>
              ) : (
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='60'
                  height='60'
                  fill='red'
                  className='bi bi-x-circle-fill'
                  viewBox='0 0 16 16'
                >
                  <path d='M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM4.646 4.646a.5.5 0 0 0 0 .708L7.293 8l-2.647 2.646a.5.5 0 0 0 .708.708L8 8.707l2.646 2.647a.5.5 0 0 0 .708-.708L8.707 8l2.647-2.646a.5.5 0 0 0-.708-.708L8 7.293 5.354 4.646a.5.5 0 0 0-.708 0z' />
                </svg>
              )}
            </div>
            <h5 className='mb-2'>{messageModal.title}</h5>
            <p>{messageModal.body}</p>
            <Button
              variant={messageModal.title === 'Success' ? 'success' : 'danger'}
              style={{
                width: '100%',
                fontWeight: '600',
                borderRadius: '10px',
                background:
                  messageModal.title === 'Success'
                    ? 'linear-gradient(135deg, #28a745, #198754)'
                    : 'linear-gradient(135deg, #dc3545, #b02a37)',
                border: 'none',
                boxShadow: '0px 4px 10px rgba(0,0,0,0.25)',
              }}
              onClick={handleCloseMessageModal}
            >
              Close
            </Button>
          </Modal.Body>
        </Modal>

        <div className='row justify-content-between align-items-center mt-3 mb-3'>
          <div className='col-md-9 col-lg-9 col-sm-12 align-items-center py-3 gap-2'>
            <Row className='g-2'>
              <Col xs='auto'>
                <InputGroup style={{ width: '300px' }}>
                  <InputGroup.Text>
                    <i className='fa fa-search'></i>
                  </InputGroup.Text>
                  <Form.Control
                    type='text'
                    placeholder='Search Branches'
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </InputGroup>
              </Col>
            </Row>
          </div>
          <div className='col-md-3 col-lg-3 col-sm-12 text-end'>
            <Button
              size='sm'
              variant='primary'
              onClick={() => setShowCreateModal(true)}
              style={{ marginRight: '2px' }}
            >
              <i className='fa fa-plus'></i> New Branch
            </Button>
            <Button size='sm' variant='success' onClick={handleExportToExcel}>
              <FaFileExcel /> Export to Excel
            </Button>
          </div>
        </div>

        {/* Branches Table */}
        <div style={{ maxHeight: '54vh', position: 'relative' }}>
          {/* Count Display */}
          {filteredBranches.length > 0 && (
            <div
              className='text-end p-2'
              style={{
                backgroundColor: '#f8f9fa',
                borderBottom: '1px solid #dee2e6',
              }}
            >
              <small className='text-muted'>
                Showing <strong>{1}</strong> to{' '}
                <strong>{filteredBranches.length}</strong> out of{' '}
                <strong>{totalRows}</strong> branches
              </small>
            </div>
          )}
          <DataTable
            columns={columns}
            data={filteredBranches}
            pagination={false}
            highlightOnHover
            striped
            dense
            persistTableHead
            fixedHeader
            fixedHeaderScrollHeight='50vh'
            noTableHead={false}
            customStyles={{
              table: {
                style: {
                  tableLayout: 'auto',
                  width: '100%',
                  maxWidth: '100%',
                },
              },
              tableWrapper: {
                style: {
                  width: '100%',
                  maxWidth: '100%',
                  overflowX: 'auto',
                },
              },
              headRow: {
                style: {
                  backgroundColor: '#f8f9fa',
                  borderBottomStyle: 'solid',
                  borderBottomWidth: '2px',
                  borderBottomColor: '#dee2e6',
                },
              },
              headCells: {
                style: {
                  fontSize: '14px',
                  fontWeight: '600',
                  paddingLeft: '8px',
                  paddingRight: '8px',
                },
              },
              cells: {
                style: {
                  wordBreak: 'break-word',
                  whiteSpace: 'normal',
                },
              },
            }}
            progressPending={loading}
            progressComponent={CustomLoadingIndicator}
            noDataComponent={CustomNoDataComponent}
          />
          {!loading && allBranches.length > 0 && (
            <div className='text-center p-3 text-muted'>
              <small>All branches loaded ({allBranches.length} total)</small>
            </div>
          )}
        </div>

        {/* Create Map Popup */}
        <MapPopup
          show={showCreateMapPopup}
          handleClose={() => setShowCreateMapPopup(false)}
          title='Select Branch Location'
          center={createMapCenter}
          setCoordinatesAndAddress={handleMapSelectForCreate}
          mapLanguage={mapLanguage}
        />

        {/* Edit Map Popup */}
        <MapPopup
          show={showEditMapPopup}
          handleClose={() => setShowEditMapPopup(false)}
          title='Select Branch Location'
          center={editMapCenter}
          setCoordinatesAndAddress={handleMapSelectForEdit}
          mapLanguage={mapLanguage}
        />

        {/* Create Branch Modal */}
        <Modal
          show={showCreateModal}
          onHide={() => setShowCreateModal(false)}
          size='lg'
        >
          <Modal.Header closeButton>
            <Modal.Title>Create New Branch</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form onSubmit={handleCreateSubmit}>
              <Row>
                <Col md={6}>
                  <Form.Group className='mb-2'>
                    <Form.Label>Name</Form.Label>
                    <Form.Control
                      name='name'
                      value={createForm.name}
                      onChange={handleCreateChange}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className='mb-2'>
                    <Form.Label>Code</Form.Label>
                    <Form.Control
                      name='code'
                      value={createForm.code}
                      onChange={handleCreateChange}
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row className='align-items-center mb-2'>
                <Col md={4}>
                  <Form.Group className='mb-2'>
                    <Form.Label>Latitude</Form.Label>
                    <Form.Control
                      name='latitude'
                      value={createForm.latitude}
                      onChange={handleCreateChange}
                      type='number'
                      step='any'
                      placeholder='Enter latitude'
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className='mb-2'>
                    <Form.Label>Longitude</Form.Label>
                    <Form.Control
                      name='longitude'
                      value={createForm.longitude}
                      onChange={handleCreateChange}
                      type='number'
                      step='any'
                      placeholder='Enter longitude'
                    />
                  </Form.Group>
                </Col>
                <Col md={4} className='mt-4'>
                  <Button
                    className='w-100'
                    variant='success'
                    onClick={openCreateMap}
                    type='button'
                  >
                    📍 Select on Map
                  </Button>
                </Col>
              </Row>

              <Form.Group className='mb-2'>
                <Form.Label>Address</Form.Label>
                <Form.Control
                  as='textarea'
                  rows={2}
                  name='address'
                  value={createForm.address}
                  onChange={handleCreateChange}
                />
                {createForm.address && (
                  <small className='text-muted'>
                    You can also edit this address after picking from the map.
                  </small>
                )}
              </Form.Group>

              <Row>
                <Col md={6}>
                  <Form.Group className='mb-2'>
                    <Form.Label>Phone</Form.Label>
                    <Form.Control
                      name='phone'
                      value={createForm.phone}
                      onChange={handleCreateChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className='mb-2'>
                    <Form.Label>Radius (km)</Form.Label>
                    <Form.Control
                      type='number'
                      step='0.01'
                      name='radius'
                      value={createForm.radius}
                      onChange={handleCreateChange}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className='mb-2'>
                    <Form.Label>Manager</Form.Label>
                    <Form.Select
                      name='managerId'
                      value={createForm.managerId}
                      onChange={handleCreateChange}
                    >
                      <option value=''>Select Manager</option>
                      {Array.isArray(managers) &&
                        managers.map((m) => (
                          <option key={m.id} value={m.id}>
                            {m.name}
                          </option>
                        ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <div className='d-flex gap-2 mt-3'>
                <Button
                  variant='secondary'
                  onClick={() => setShowCreateModal(false)}
                  className='flex-fill'
                >
                  Cancel
                </Button>
                <Button
                  type='submit'
                  variant='primary'
                  className='flex-fill'
                  disabled={createLoading}
                >
                  {createLoading ? (
                    <>
                      <Spinner animation='border' size='sm' className='me-2' />
                      Creating...
                    </>
                  ) : (
                    'Create Branch'
                  )}
                </Button>
              </div>
            </Form>
          </Modal.Body>
        </Modal>

        {/* Edit Branch Modal */}
        <Modal
          show={showEditModal}
          onHide={() => setShowEditModal(false)}
          size='lg'
        >
          <Modal.Header closeButton>
            <Modal.Title>Edit Branch</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {currentBranch && (
              <Form onSubmit={handleUpdate}>
                <Row>
                  <Col md={6}>
                    <Form.Group className='mb-2'>
                      <Form.Label>Name</Form.Label>
                      <Form.Control
                        name='name'
                        value={currentBranch.name || ''}
                        onChange={handleEditChange}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className='mb-2'>
                      <Form.Label>Code</Form.Label>
                      <Form.Control
                        name='code'
                        value={currentBranch.code || ''}
                        onChange={handleEditChange}
                        required
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row className='align-items-center mb-2'>
                  <Col md={4}>
                    <Form.Group className='mb-2'>
                      <Form.Label>Latitude</Form.Label>
                      <Form.Control
                        name='lat'
                        value={currentBranch.lat || ''}
                        onChange={handleEditChange}
                        type='number'
                        step='any'
                        placeholder='Enter latitude'
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className='mb-2'>
                      <Form.Label>Longitude</Form.Label>
                      <Form.Control
                        name='lng'
                        value={currentBranch.lng || ''}
                        onChange={handleEditChange}
                        type='number'
                        step='any'
                        placeholder='Enter longitude'
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4} className='mt-4'>
                    <Button
                      className='w-100'
                      variant='success'
                      onClick={openEditMap}
                    >
                      📍 Select on Map
                    </Button>
                  </Col>
                </Row>

                <Form.Group className='mb-2'>
                  <Form.Label>Address</Form.Label>
                  <Form.Control
                    as='textarea'
                    rows={2}
                    name='address'
                    value={currentBranch.address || ''}
                    onChange={handleEditChange}
                  />
                  {currentBranch.address && (
                    <small className='text-muted'>
                      You can also edit this address after picking from the map.
                    </small>
                  )}
                </Form.Group>

                <Row>
                  <Col md={6}>
                    <Form.Group className='mb-2'>
                      <Form.Label>Phone</Form.Label>
                      <Form.Control
                        name='phone'
                        value={currentBranch.phone || ''}
                        onChange={handleEditChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className='mb-2'>
                      <Form.Label>Radius (km)</Form.Label>
                      <Form.Control
                        type='number'
                        step='0.01'
                        name='radius'
                        value={currentBranch.radius || ''}
                        onChange={handleEditChange}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className='mb-2'>
                      <Form.Label>Manager</Form.Label>
                      <Form.Select
                        name='manager_id'
                        value={currentBranch.manager_id || ''}
                        onChange={handleEditChange}
                      >
                        <option value=''>Select Manager</option>
                        {Array.isArray(managers) &&
                          managers.map((m) => (
                            <option key={m.id} value={m.id}>
                              {m.name}
                            </option>
                          ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                <Button type='submit' className='w-100 mt-3' variant='success'>
                  Update Branch
                </Button>
              </Form>
            )}
          </Modal.Body>
        </Modal>
      </Container>
    </div>
  );
};

export default AdminBranches;
